import { MAX_LATEST_VISITORS_LIMIT, tabNames, VISITORS_SELECTED_POINT_PROPERTY } from '@va/constants';
import { setTabProperty } from '@va/dashboard/actions/ui';
import { getTabProperty } from '@va/dashboard/selectors/ui';
import { useSessionMetricsFilters } from '@va/dashboard/shared/filters';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { usePageHeaderProps } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { AppliedFilters, FiltersContextProvider, useFiltersContext } from '@va/shared/feature-filters';
import { DataViewOption, ReportBlockNodes } from '@va/types/report-block';
import { SortingDirection } from '@va/types/requests';
import { VisitorsMapSelectedPoint } from '@va/types/visitors';
import { ReportBlock, ReportBlockNodeIds } from '@va/ui/components/report-block';
import { useRefreshContext } from '@va/util/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LeafletMapPanel, { LeafletMapRawPoint } from '../leaflet-map/LeafletMap';
import { useVisitorsLocation } from './apiClient';

const CENTERED_MAP_ZOOM = 14;

const LatestVisitorsMap = () => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { allFilters } = useSessionMetricsFilters();
  const buildReportBlockNode = useReportBlockNodeBuilder();

  const nodes = useMemo<ReportBlockNodes>(
    () => ({
      filterButton: buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON),
      appliedFilters: <AppliedFilters keepHorizontalDivider={false} />,
    }),
    [buildReportBlockNode],
  );

  const visualizationComponents = useMemo(
    () => ({
      [DataViewOption.map]: RBMap,
    }),
    [],
  );

  useEffect(() => {
    return () => {
      dispatch(setTabProperty(tabNames.latestVisitorsMap, VISITORS_SELECTED_POINT_PROPERTY, undefined));
    };
  }, [dispatch]);

  return (
    <FiltersContextProvider reportBlockId={'latest-visitors-map-report-block'} allFilterOptions={allFilters}>
      <ReportBlock
        id='latest-visitors-map-report-block'
        title={translate('all.latestVisitors.map.reportBlockTitle')}
        titleTooltip={translate('all.latestVisitors.map.reportBlockTitleTooltip')}
        visualization={{ selectedView: DataViewOption.map }}
        visualizationComponents={visualizationComponents}
        nodes={nodes}
      />
    </FiltersContextProvider>
  );
};

const RBMap = () => {
  const { createApiConditions } = useFiltersContext();
  const {
    data: fetchedData,
    mutate,
    isValidating,
  } = useVisitorsLocation({
    pagination: { page: 1, pageSize: MAX_LATEST_VISITORS_LIMIT },
    order: [{ member: 'sessionStartUnix', direction: SortingDirection.Desc }],
    where: { and: createApiConditions() },
    config: {
      onSuccess(d) {
        setData(d);
        mutate(undefined, { revalidate: false });
      },
      revalidateOnMount: true,
    },
  });
  const [data, setData] = useState(fetchedData);

  const { isManualRefreshing } = useRefreshContext();

  const pageHeaderProps = useMemo(() => ({ refreshFn: mutate }), [mutate]);

  usePageHeaderProps(pageHeaderProps);

  const selectedPoint: VisitorsMapSelectedPoint | undefined = useSelector((state) =>
    getTabProperty(state, tabNames.latestVisitorsMap, VISITORS_SELECTED_POINT_PROPERTY),
  );

  const isPointSelected = useCallback(
    (point: LeafletMapRawPoint) => point.sessionKey === selectedPoint?.sessionId,
    [selectedPoint],
  );

  return (
    <LeafletMapPanel
      center={selectedPoint?.location}
      circleRadius={3000}
      inProgress={isManualRefreshing || isValidating}
      items={data ?? []}
      zoom={selectedPoint ? CENTERED_MAP_ZOOM : undefined}
      isPointSelected={isPointSelected}
    />
  );
};

export default LatestVisitorsMap;
